import React, { useEffect, useState, useCallback } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { FilterOutlined, InboxOutlined, WalletOutlined } from "@ant-design/icons";
import axios from "axios";

import GoToHome from "../../Components/GoToHome";
import { getAccessToken, apiUrl } from "../../common/apis";
import styles from "./wallet.module.css";

const { RangePicker } = DatePicker;

function Wallet() {
  const [ordersCount, setOrdersCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [nextPage, setNextPage] = useState("");
  const [activePreset, setActivePreset] = useState("7days");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const dateFormat = "DD-MM-YYYY";

  const getOrderHistory = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    const fromDateQuery = dateRange && dateRange[0]
      ? dateRange[0].format("YYYY-MM-DD 00:00:00")
      : dayjs().subtract(7, "day").format("YYYY-MM-DD 00:00:00");
    const toDateQuery = dateRange && dateRange[1]
      ? dateRange[1].format("YYYY-MM-DD 23:59:59")
      : dayjs().format("YYYY-MM-DD 23:59:59");

    const config = {
      method: "get",
      url: `${apiUrl}accounts/?limit=25&from_date=${fromDateQuery}&to_date=${toDateQuery}`,
      headers: { Authorization: token },
    };

    axios(config)
      .then((res) => {
        if (res.data) {
          setOrdersCount(res.data.count || 0);
          setOrders(res.data.results || []);
          setNextPage(res.data.next || "");
        }
      })
      .catch(() => {});
  }, [dateRange]);

  useEffect(() => {
    if (!getAccessToken()) return;
    getOrderHistory();
  }, [getOrderHistory]);

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const today = dayjs();
    if (preset === "today") {
      setDateRange([today, today]);
    } else if (preset === "yesterday") {
      const yesterday = today.subtract(1, "day");
      setDateRange([yesterday, yesterday]);
    } else if (preset === "7days") {
      setDateRange([today.subtract(7, "day"), today]);
    } else if (preset === "month") {
      setDateRange([today.startOf("month"), today]);
    }
  };

  const getOrderTime = (time) => {
    if (!time) return "";
    const utcDateTime = new Date(time);
    const istDateTime = utcDateTime
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    return istDateTime;
  };

  const loadMoreOrders = () => {
    if (!nextPage) return;
    setIsLoadingMore(true);
    let config = {
      method: "get",
      url: nextPage.startsWith("http") ? nextPage : `${apiUrl}${nextPage}`,
      headers: { Authorization: getAccessToken() },
    };

    axios(config)
      .then((res) => {
        setIsLoadingMore(false);
        if (res.data?.results) {
          setOrders((prev) => [...prev, ...res.data.results]);
          setNextPage(res.data.next || "");
        }
      })
      .catch(() => {
        setIsLoadingMore(false);
      });
  };

  // Calculate totals
  const totalDeliveryCharges = orders.reduce(
    (sum, item) => sum + (Number(item.delivery_charge) || 0),
    0
  );
  const totalTips = orders.reduce(
    (sum, item) => sum + (Number(item.delivery_tip) || 0),
    0
  );
  const totalEarnings = totalDeliveryCharges + totalTips;

  return (
    <div className={styles.walletContainer}>
      <GoToHome />

      {/* Header */}
      <div className={styles.walletHeader}>
        <h2 className={styles.walletTitle}>My Wallet</h2>
        {ordersCount > 0 && (
          <span className={styles.totalBadge}>{`${ordersCount} Orders`}</span>
        )}
      </div>

      {/* Earnings Hero Banner */}
      <div className={styles.earningsHeroCard}>
        <div className={styles.heroTop}>
          <p className={styles.heroLabel}>Total Period Earnings</p>
          <WalletOutlined style={{ fontSize: "20px", color: "#94a3b8" }} />
        </div>
        <h1 className={styles.heroTotal}>₹{totalEarnings.toFixed(2)}</h1>

        <div className={styles.heroBreakdown}>
          <div className={styles.breakdownCol}>
            <span>Delivery Charges</span>
            <strong className={styles.deliveryChargeColor}>
              ₹{totalDeliveryCharges.toFixed(2)}
            </strong>
          </div>
          <div className={styles.breakdownCol}>
            <span>Customer Tips</span>
            <strong className={styles.tipColor}>
              ₹{totalTips.toFixed(2)}
            </strong>
          </div>
        </div>
      </div>

      {/* Date Filter Card */}
      <div className={styles.filterCard}>
        <span className={styles.filterLabel}>
          <FilterOutlined style={{ marginRight: "6px" }} />
          Filter Period
        </span>

        {/* Quick Presets */}
        <div className={styles.presetContainer}>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "today" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("today")}
          >
            Today
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "yesterday" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("yesterday")}
          >
            Yesterday
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "7days" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("7days")}
          >
            Last 7 Days
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "month" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("month")}
          >
            This Month
          </button>
        </div>

        {/* Single Unified RangePicker */}
        <div className={styles.singlePickerRow}>
          <RangePicker
            value={dateRange}
            format={dateFormat}
            allowClear={false}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]]);
                setActivePreset("custom");
              }
            }}
          />
        </div>
      </div>

      {/* Transactions List */}
      <div className={styles.transactionList}>
        {orders && orders.length > 0 ? (
          orders.map((orderItem) => (
            <div key={orderItem.id || orderItem.order} className={styles.orderItemContainer}>
              <div className={styles.cardTop}>
                <span className={styles.orderId}>{`#FDR ${orderItem.order}`}</span>
                <span className={styles.orderDate}>{getOrderTime(orderItem.date)}</span>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.earningRow}>
                  <span>Delivery Charge Earned:</span>
                  <strong style={{ color: "#d9363e" }}>
                    ₹{orderItem.delivery_charge}
                  </strong>
                </div>

                {Number(orderItem.delivery_tip) > 0 && (
                  <div className={styles.earningRow}>
                    <span>Customer Tip Earned:</span>
                    <strong style={{ color: "#16a34a" }}>
                      +₹{orderItem.delivery_tip}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <InboxOutlined />
            </div>
            <p className={styles.emptyText}>No earnings found in this date range.</p>
          </div>
        )}
      </div>

      {nextPage && (
        <button
          className={styles.loadMoreBtn}
          onClick={loadMoreOrders}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? "Loading..." : "Load More Transactions"}
        </button>
      )}
    </div>
  );
}

export default Wallet;
